// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 81eef0ba-4467-456e-a145-bf04be76e8f9

// Primordia theme system — Light / Dark / System, like a native app.
//
// Everything theme-related lives in this one file on purpose: the CMDB screens
// currently hardcode brand colours inline (#f5f6f8 backgrounds, #021F4B text,
// #12325f section headers, a white AppBar). Centralising the palettes here gives
// us one place to fix those as screens are migrated to theme-aware values.
//
// Exports:
//   PrimordiaThemeProvider (default) — wraps children in MUI ThemeProvider + CssBaseline
//   useThemeMode()                   — { mode, setMode, resolvedMode }
//   ThemeModeToggle                  — AppBar-ready Light / Dark / System control
//   sectionHeaderColor(theme)        — the hardcoded '#12325f' navy, per mode

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';

// ---------------------------------------------------------------------------
// Brand tokens
// ---------------------------------------------------------------------------

// Optimum Healthcare IT brand palette, taken from the live site
// (optimumhealthcareit.com) rather than approximated. The previous values here
// were close guesses; these are the actual ones, with their measured contrast
// against white and against this theme's dark surface (#16202E) recorded so a
// later change can be judged rather than eyeballed.
const BRAND = {
  navy: '#021F4B',      // 16.15:1 on white  ·  1.02:1 on dark — light-mode text/fills only
  blue: '#0D5B9A',      // 7.05:1 on white   ·  2.33:1 on dark — see darkBlue below
  // The most-used colour on the Optimum site, and absent from this app until now.
  //
  // ORANGE IS NEVER A BACKGROUND. That is a direct instruction from Frank and it
  // is also what the numbers support: surfaces stay white in light mode and the
  // dark navy-grey in dark mode, and the orange appears only as INK — an icon, a
  // rule, an underline, a border, a chip outline, a small emphasised label.
  // Filling a panel with it would fight the navy rather than accent it, and would
  // leave any text placed on top with nowhere legible to go.
  //
  // Its contrast confirms the same limit from the other direction: 3.17:1 on
  // white passes AA for large text and UI components but FAILS for body copy, so
  // it can outline and emphasise but must never carry a paragraph.
  orange: '#F2633D',    // 3.17:1 on white   ·  5.18:1 on dark
  paleBlue: '#ECF7FF',  // 1.09:1 on white   · 15.09:1 on dark — a dark-mode text colour
  // The brand blue is DARKER than the value it replaces, so it reads worse on a
  // dark surface, not better (2.33:1 against the old 3.10:1). Dark mode
  // therefore keeps a lifted tint rather than the literal brand colour: matching
  // the brand exactly at the cost of legibility is not matching the brand.
  lightBlue: '#3D9AD1',
  grey: '#5C6B7A',
  sectionHeaderLight: '#021F4B', // now the true brand navy
  sectionHeaderDark: '#8FB8DE',  // same role, lifted for dark backgrounds (7.7:1 on paper)
};

// Dark surfaces. Deliberately NOT an inversion of the light theme: a flipped
// #f5f6f8 would be near-black and make every elevation step invisible. Instead
// we use two cool navy-tinted greys so the app keeps its Optimum feel, with the
// paper surface lighter than the page so cards/tables read as raised.
const DARK = {
  bg: '#0F1621',
  paper: '#16202E',
  paperElevated: '#1C283A', // hover / menus / second-level surfaces
  textPrimary: '#E6EDF5', // 13.7:1 on paper, 15.7:1 on bg — well past WCAG AA (4.5:1)
  textSecondary: '#9AA6B2', // 6.5:1 on paper — AA for body text, not just large text
  divider: 'rgba(230, 237, 245, 0.10)', // low-alpha instead of a solid light line: solid
  border: '#26344A', //  greys glare against dark surfaces at normal viewing brightness
};

const LIGHT = {
  bg: '#f5f6f8', // matches the hardcoded page background in CmdbApp.js
  paper: '#ffffff',
  textPrimary: BRAND.navy, // 14.7:1 on white
  textSecondary: BRAND.grey, // 5.5:1 on white
  border: '#e0e0e0',
};

const STORAGE_KEY = 'primordia.theme';
const MODES = ['light', 'dark', 'system'];

// ---------------------------------------------------------------------------
// Preference persistence
//
// Read/write are isolated in these two functions ON PURPOSE. When the theme
// preference moves onto the user profile (Cognito custom attribute or the
// Primordia user record keyed off getSession() in ./auth), swap the bodies here
// — ideally to an async read hydrated at boot with the localStorage value as the
// offline fallback — and nothing else in the app has to change.
// ---------------------------------------------------------------------------

function readThemePreference() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return MODES.includes(raw) ? raw : 'system';
  } catch (e) {
    // Private browsing / storage disabled — fall back to following the OS.
    return 'system';
  }
}

function writeThemePreference(mode) {
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch (e) {
    // Non-fatal: the choice just won't survive a reload.
  }
}

// ---------------------------------------------------------------------------
// Theme construction
// ---------------------------------------------------------------------------

function buildPalette(mode) {
  const isDark = mode === 'dark';
  if (!isDark) {
    return {
      mode: 'light',
      primary: { main: BRAND.blue, dark: BRAND.navy, light: BRAND.lightBlue, contrastText: '#ffffff' },
      // Custom token so screens can write sx={{ color: 'sectionHeader.main' }}
      // instead of hardcoding the navy, which was invisible in dark mode.
      sectionHeader: { main: BRAND.sectionHeaderLight },
      // M17: dozens of screens fill small chips/swatches with a hardcoded pale
      // blue ('#eef3f9', '#e8f0fe', '#eef4fb', … — near-identical near-white
      // tints) under sectionHeader-coloured text. Fine in light mode by
      // accident; on dark that same literal stays near-white while the text
      // becomes the light-blue dark-mode token, ~2:1. One token standardises
      // the tint and gives dark mode a real (dark) surface to pair it with.
      // Text 'sectionHeader.main' on this: 14.7:1 light (close to its old ad
      // hoc literals, measured against #EEF3F9).
      chipMuted: { main: '#EEF3F9' },
      secondary: { main: BRAND.navy, contrastText: '#ffffff' },
      background: { default: LIGHT.bg, paper: LIGHT.paper },
      text: { primary: LIGHT.textPrimary, secondary: LIGHT.textSecondary },
      divider: LIGHT.border,
    };
  }
  return {
    mode: 'dark',
    // Same role as the light-mode navy, lifted so it stays readable on dark
    // surfaces (7.7:1 on paper). Screens reference 'sectionHeader.main'.
    sectionHeader: { main: BRAND.sectionHeaderDark },
    // Dark counterpart of chipMuted above: reuses the existing elevated
    // surface rather than inventing a new one. Text 'sectionHeader.main'
    // (#8FB8DE) on #1C283A measures 7.13:1 — AA for body text, close to the
    // 7.7:1 it gets on plain paper.
    chipMuted: { main: DARK.paperElevated },
    // #0D5B9A only reaches ~2.2:1 against #16202E, so it fails AA as text/icon
    // colour on dark. We promote the brand's own lighter tint to `main` (5.2:1 on
    // paper) and keep the true brand blue available as `dark` for accents.
    primary: {
      main: BRAND.lightBlue,
      light: '#6FB6E0',
      dark: BRAND.blue,
      // White on #3D9AD1 is only 3.1:1; near-black on it is 5.9:1, so filled
      // buttons stay AA-compliant in dark mode.
      contrastText: '#0B1220',
    },
    secondary: { main: '#8FB8DE', contrastText: '#0B1220' },
    background: { default: DARK.bg, paper: DARK.paper },
    text: { primary: DARK.textPrimary, secondary: DARK.textSecondary, disabled: 'rgba(230,237,245,0.38)' },
    divider: DARK.divider,
    // Status colours lightened so they clear AA on the dark surfaces; the stock
    // MUI dark defaults are tuned for #121212, not our navy-tinted greys.
    error: { main: '#F2717F' },
    warning: { main: '#F5B851' },
    success: { main: '#6FCF8E' },
    info: { main: '#6FB6E0' },
    action: {
      hover: 'rgba(230,237,245,0.06)',
      selected: 'rgba(61,154,209,0.16)',
    },
  };
}

function buildTheme(mode) {
  const isDark = mode === 'dark';
  const palette = buildPalette(mode);

  return createTheme({
    palette,
    typography: { fontFamily: '"Segoe UI", Roboto, Helvetica, Arial, sans-serif' },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Tells the browser to render native widgets (scrollbars, date pickers,
          // form controls, autofill) in the matching scheme. Without this the
          // scrollbars stay bright white and ruin an otherwise dark screen.
          ':root': { colorScheme: isDark ? 'dark' : 'light' },
        },
      },

      // The CMDB AppBar is a white bar with a 3px Optimum-blue rule. In dark mode
      // we swap white for the paper surface and keep the brand rule identical, so
      // the chrome reads the same at a glance in both schemes.
      MuiAppBar: {
        defaultProps: { color: 'inherit' },
        styleOverrides: {
          root: {
            backgroundColor: isDark ? DARK.paper : LIGHT.paper,
            color: isDark ? DARK.textPrimary : BRAND.navy,
            backgroundImage: 'none', // kill MUI's dark elevation overlay tint
            borderBottom: `3px solid ${BRAND.blue}`,
          },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none', // same reason: we want flat, controlled surfaces
            ...(isDark && { border: `1px solid ${DARK.divider}` }),
          },
          // Menus / popovers sit above paper, so they get the elevated surface.
          elevation8: isDark ? { backgroundColor: DARK.paperElevated } : undefined,
          elevation16: isDark ? { backgroundColor: DARK.paperElevated } : undefined,
          elevation24: isDark ? { backgroundColor: DARK.paperElevated } : undefined,
        },
      },

      // Chips are used everywhere as tags/status pills. The default filled chip is
      // far too bright on a dark page; outline-ish low-alpha fills read better.
      MuiChip: {
        styleOverrides: {
          root: isDark
            ? { backgroundColor: 'rgba(230,237,245,0.08)', color: DARK.textPrimary }
            : undefined,
          outlined: isDark ? { borderColor: DARK.border } : undefined,
        },
      },

      // Table rules are the single biggest source of glare in dark mode — the MUI
      // default is a light rgba(224,224,224) line on every cell.
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottomColor: isDark ? DARK.divider : LIGHT.border },
          head: {
            backgroundColor: isDark ? DARK.paper : LIGHT.paper,
            color: isDark ? BRAND.sectionHeaderDark : BRAND.sectionHeaderLight,
            fontWeight: 700,
          },
        },
      },

      MuiDivider: {
        styleOverrides: { root: { borderColor: isDark ? DARK.divider : LIGHT.border } },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: isDark ? { borderColor: DARK.border } : undefined,
        },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: isDark ? { backgroundColor: DARK.paperElevated, color: DARK.textPrimary } : undefined,
        },
      },
    },
  });
}

// Section headers are hardcoded to '#12325f' across the CMDB screens. Use this
// helper (inside sx/useTheme) instead of the literal so they stay legible in dark
// mode: sx={{ color: (t) => sectionHeaderColor(t) }}
export function sectionHeaderColor(theme) {
  return theme && theme.palette && theme.palette.mode === 'dark'
    ? BRAND.sectionHeaderDark
    : BRAND.sectionHeaderLight;
}

// ---------------------------------------------------------------------------
// Context + provider
// ---------------------------------------------------------------------------

const ThemeModeContext = createContext({
  mode: 'system',
  setMode: () => {},
  resolvedMode: 'light',
});

// Reads the OS preference once, defensively — matchMedia is missing in jsdom and
// in very old browsers, and we never want the theme layer to be able to crash the
// app shell.
function getSystemMode() {
  try {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  } catch (e) {
    return 'light';
  }
}

export function PrimordiaThemeProvider({ children }) {
  // Lazy initialisers so storage/matchMedia are touched exactly once, on mount,
  // rather than on every render.
  const [mode, setModeState] = useState(readThemePreference);
  const [systemMode, setSystemMode] = useState(getSystemMode);

  // Live OS tracking. This is what makes 'system' behave like a native app rather
  // than a one-shot read at boot: macOS/Windows can flip the scheme on a schedule
  // (or the user toggles it), and the media query fires while we're still mounted.
  //
  // We subscribe unconditionally instead of only when mode === 'system' so that
  // `systemMode` is always current — switching back to 'system' then applies the
  // right scheme instantly, with no flash of the stale one.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e) => setSystemMode(e.matches ? 'dark' : 'light');

    // Re-sync on subscribe: the OS may have changed between the initial useState
    // read and this effect running (StrictMode double-invoke, slow hydration).
    setSystemMode(mql.matches ? 'dark' : 'light');

    // Safari < 14 and older Edge only implement the deprecated addListener API,
    // so feature-detect and, importantly, remove with the matching remover —
    // otherwise the listener leaks and fires against an unmounted tree.
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  const setMode = useCallback((next) => {
    if (!MODES.includes(next)) return;
    setModeState(next);
    writeThemePreference(next);
  }, []);

  // 'system' is a preference, not a colour — resolve it to a concrete scheme here
  // so every consumer (and the MUI theme) only ever sees 'light' | 'dark'.
  const resolvedMode = mode === 'system' ? systemMode : mode;

  // Rebuilding the theme is not cheap; only do it when the resolved scheme flips.
  const theme = useMemo(() => buildTheme(resolvedMode), [resolvedMode]);

  const ctx = useMemo(
    () => ({ mode, setMode, resolvedMode }),
    [mode, setMode, resolvedMode]
  );

  return (
    <ThemeModeContext.Provider value={ctx}>
      <ThemeProvider theme={theme}>
        {/* CssBaseline paints palette.background.default on <body>, which is what
            makes the whole page (not just MUI surfaces) go dark. */}
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

// { mode: 'light'|'dark'|'system', setMode(m), resolvedMode: 'light'|'dark' }
// Safe to call outside the provider — it degrades to a read-only light theme
// rather than throwing, so a stray consumer can never white-screen the app.
export function useThemeMode() {
  return useContext(ThemeModeContext);
}

// ---------------------------------------------------------------------------
// Toggle control
// ---------------------------------------------------------------------------

const MODE_OPTIONS = [
  { value: 'light', label: 'Light', Icon: LightModeIcon },
  { value: 'dark', label: 'Dark', Icon: DarkModeIcon },
  { value: 'system', label: 'System', Icon: SettingsBrightnessIcon },
];

// Compact AppBar control: one icon button showing the *effective* scheme, with a
// menu to pick Light / Dark / System. Uses color="inherit" so it picks up the
// AppBar's text colour in both schemes.
export function ThemeModeToggle({ size = 'small', sx }) {
  const { mode, setMode, resolvedMode } = useThemeMode();
  const [anchorEl, setAnchorEl] = useState(null);

  const active = MODE_OPTIONS.find((o) => o.value === mode) || MODE_OPTIONS[2];
  // When following the system we show the scheme actually in effect, so the
  // button never contradicts what the user is looking at.
  const ButtonIcon = mode === 'system'
    ? (resolvedMode === 'dark' ? DarkModeIcon : LightModeIcon)
    : active.Icon;

  const close = () => setAnchorEl(null);
  const choose = (value) => { setMode(value); close(); };

  return (
    <>
      <Tooltip title={`Theme: ${active.label}${mode === 'system' ? ` (${resolvedMode})` : ''}`}>
        <IconButton
          size={size}
          color="inherit"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label="Change theme"
          aria-haspopup="true"
          sx={sx}
        >
          <ButtonIcon fontSize={size === 'small' ? 'small' : 'medium'} />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close}>
        {MODE_OPTIONS.map(({ value, label, Icon }) => (
          <MenuItem key={value} selected={mode === value} onClick={() => choose(value)}>
            <ListItemIcon><Icon fontSize="small" /></ListItemIcon>
            <ListItemText
              primary={label}
              secondary={value === 'system' ? `Following OS · ${resolvedMode}` : null}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default PrimordiaThemeProvider;
